import React, { useState } from "react";
import { Organization, OrgForm, OrgPlan, Status } from "../../types";
import { useApp } from "../../context/AppContext";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Table, { Column } from "../../components/ui/Table";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import { Input, Select, ColorPicker } from "../../components/ui/FormFields";

const BLANK: OrgForm = { name: "", domain: "", primaryColor: "#6366f1", plan: "pro", status: "active" };

const OrganizationsPage: React.FC = () => {
  const { orgs, addOrg, updateOrg, deleteOrg, loading } = useApp();
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editOrg, setEditOrg] = useState<Organization | null>(null);
  const [deleteOrgItem, setDeleteOrgItem] = useState<Organization | null>(null);
  const [form, setForm] = useState<OrgForm>(BLANK);
  const [saving, setSaving] = useState(false);

  const handleFormChange = (key: keyof OrgForm) => (value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const openNew = () => { 
    setEditOrg(null); 
    setForm(BLANK); 
    setShowModal(true); 
  };
  
  const openEdit = (org: Organization) => { 
    setEditOrg(org); 
    setForm({ 
      name: org.name, 
      domain: org.domain, 
      primaryColor: org.primaryColor, 
      plan: org.plan, 
      status: org.status 
    }); 
    setShowModal(true); 
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editOrg) {
        await updateOrg(editOrg.id, form);
      } else {
        const now = new Date().toISOString();
        await addOrg({
          ...form,
          adminCount: 0,
          candidateCount: 0,
          createdAt: now
        });
      }
      setShowModal(false);
      setForm(BLANK);
    } catch (error) {
      console.error("Error saving organization:", error);
      alert("Failed to save organization. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteOrgItem) {
      setSaving(true);
      try {
        await deleteOrg(deleteOrgItem.id);
        setDeleteOrgItem(null);
      } catch (error) {
        console.error("Error deleting organization:", error);
        alert("Failed to delete organization. Please try again.");
      } finally {
        setSaving(false);
      }
    }
  };

  const formatDate = (date: any): string => {
    if (!date) return "";
    if (date?.toDate) return date.toDate().toLocaleDateString();
    if (typeof date === "string") return new Date(date).toLocaleDateString();
    if (date instanceof Date) return date.toLocaleDateString();
    return "";
  };

  const filtered = orgs.filter(org =>
    org.name.toLowerCase().includes(search.toLowerCase()) ||
    org.domain.toLowerCase().includes(search.toLowerCase())
  );

  const cols: Column<Organization>[] = [
    { 
      key: "name", 
      label: "Name", 
      render: (value, record) => (
        <div className="flex items-center gap-2">
          <div 
            className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
            style={{ background: record.primaryColor }}
          >
            {record.name.charAt(0)}
          </div>
          <span className="font-semibold text-gray-800">{value}</span>
        </div>
      )
    },
    { 
      key: "domain", 
      label: "Domain", 
      render: (value) => <span className="font-mono text-xs text-indigo-600">{value}</span> 
    },
    { 
      key: "plan", 
      label: "Plan", 
      render: (value) => <Badge text={value as string} /> 
    },
    { 
      key: "status", 
      label: "Status", 
      render: (value) => <Badge text={value as string} /> 
    },
    { key: "adminCount", label: "Admins" },
    { key: "candidateCount", label: "Candidates" },
    { key: "createdAt", label: "Created", render: (value) => formatDate(value) },
  ];

  if (loading) {
    return (
      <PageShell title="Organizations" subtitle="Manage all tenant organizations">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell 
      title="Organizations" 
      subtitle="Manage all tenant organizations" 
      action={{ label: "Add Organization", onClick: openNew }}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total" value={orgs.length} icon="🏢" color="#6366f1" />
        <StatCard label="Active" value={orgs.filter(o => o.status === "active").length} icon="✅" color="#10b981" />
        <StatCard label="Inactive" value={orgs.filter(o => o.status === "inactive").length} icon="⛔" color="#ef4444" />
        <StatCard label="Candidates" value={orgs.reduce((sum, org) => sum + (org.candidateCount || 0), 0)} icon="👥" color="#f59e0b" />
      </div>

      <GlassCard className="p-5">
        <div className="mb-4">
          <Input 
            value={search} 
            onChange={setSearch} 
            placeholder="🔍 Search by name or domain…" 
          />
        </div>
        <Table 
          columns={cols} 
          data={filtered} 
          onEdit={openEdit} 
          onDelete={(org) => setDeleteOrgItem(org)} 
        />
      </GlassCard>

      <Modal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={editOrg ? "Edit Organization" : "New Organization"}
      >
        <div className="space-y-4">
          <Input 
            label="Organization Name" 
            value={form.name} 
            onChange={handleFormChange("name")} 
            required 
          />
          <Input 
            label="Domain" 
            value={form.domain} 
            onChange={handleFormChange("domain")} 
            placeholder="company.com" 
            required 
          />
          <div className="grid grid-cols-2 gap-4">
            <Select 
              label="Plan" 
              value={form.plan} 
              onChange={handleFormChange("plan")}
              options={[
                { value: "basic", label: "Basic" }, 
                { value: "pro", label: "Pro" }, 
                { value: "enterprise", label: "Enterprise" }
              ]} 
            />
            <Select 
              label="Status" 
              value={form.status} 
              onChange={handleFormChange("status")}
              options={[
                { value: "active", label: "Active" }, 
                { value: "inactive", label: "Inactive" }
              ]} 
            />
          </div>
          <ColorPicker 
            label="Brand Color" 
            value={form.primaryColor} 
            onChange={handleFormChange("primaryColor")} 
          />
          <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>
              {saving ? "Saving..." : (editOrg ? "Update" : "Create")} Organization
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog 
        isOpen={!!deleteOrgItem} 
        onClose={() => setDeleteOrgItem(null)}
        onConfirm={handleDelete}
        title="Delete Organization" 
        message={`Permanently delete "${deleteOrgItem?.name}"? This cannot be undone.`} 
      />
    </PageShell>
  );
};

export default OrganizationsPage;